import React from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorageIcon from '@mui/icons-material/Storage';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { PageName } from '../types';
import { isAdmin as checkIsAdmin, getThemeMode, setThemeMode } from '../utils/cookies';

// Custom SVG icons for each task type
export const MmcIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 36L12 24H52L56 36" stroke="#1976d2" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="6" y="36" width="52" height="16" rx="4" fill="#1976d2"/>
    <path d="M16 36L20 28H44L48 36" fill="#e3f2fd"/>
    <circle cx="18" cy="52" r="6" fill="#424242"/>
    <circle cx="46" cy="52" r="6" fill="#424242"/>
  </svg>
);

export const DetIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="8" width="56" height="48" rx="2" stroke="#9c27b0" strokeWidth="3" strokeDasharray="6 3"/>
    <rect x="12" y="24" width="40" height="20" rx="4" fill="#ce93d8"/>
    <rect x="2" y="6" width="8" height="8" rx="2" fill="#9c27b0"/>
    <rect x="54" y="6" width="8" height="8" rx="2" fill="#9c27b0"/>
    <rect x="2" y="50" width="8" height="8" rx="2" fill="#9c27b0"/>
    <rect x="54" y="50" width="8" height="8" rx="2" fill="#9c27b0"/>
  </svg>
);

export const PlateIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="16" width="56" height="32" rx="4" fill="#ff9800"/>
    <rect x="8" y="20" width="48" height="24" rx="2" fill="#fff8e1"/>
    <rect x="12" y="28" width="8" height="10" rx="1" fill="#e65100"/>
    <rect x="22" y="28" width="8" height="10" rx="1" fill="#e65100"/>
    <rect x="34" y="28" width="8" height="10" rx="1" fill="#e65100"/>
    <rect x="44" y="28" width="8" height="10" rx="1" fill="#e65100"/>
  </svg>
);

export const DRAWER_WIDTH = 240;
export const COLLAPSED_WIDTH = 56;

interface DrawerNavigationProps {
  currentPage: PageName;
  onNavigate: (page: PageName) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  page: PageName;
  label: string;
  icon: React.ReactNode;
  indent?: boolean;
}

const searchNavItems: NavItem[] = [
  { page: 'searchMmc', label: 'MMC', icon: <MmcIcon size={24} /> },
  { page: 'seriesEditor', label: 'Series', icon: <MmcIcon size={20} />, indent: true },
  { page: 'superSeriesEditor', label: 'Super Series', icon: <MmcIcon size={20} />, indent: true },
  { page: 'flagsMmc', label: 'MMC Flags', icon: <LocalOfferIcon fontSize="small" />, indent: true },
  { page: 'datasetsMmc', label: 'MMC Datasets', icon: <StorageIcon fontSize="small" />, indent: true },
  { page: 'searchDet', label: 'Detection', icon: <DetIcon size={24} /> },
  { page: 'flagsDet', label: 'Det Flags', icon: <LocalOfferIcon fontSize="small" />, indent: true },
  { page: 'datasetsDet', label: 'Det Datasets', icon: <StorageIcon fontSize="small" />, indent: true },
  { page: 'searchPlate', label: 'Plate', icon: <PlateIcon size={24} /> },
  { page: 'flagsPlate', label: 'Plate Flags', icon: <LocalOfferIcon fontSize="small" />, indent: true },
  { page: 'datasetsPlate', label: 'Plate Datasets', icon: <StorageIcon fontSize="small" />, indent: true },
];

const statsNavItems: NavItem[] = [
  { page: 'statsMmc', label: 'MMC', icon: <MmcIcon size={24} /> },
  { page: 'statsMmcModelYear', label: 'Model x Year', icon: <MmcIcon size={20} />, indent: true },
  { page: 'statsDet', label: 'Detection', icon: <DetIcon size={24} /> },
  { page: 'statsPlate', label: 'Plate', icon: <PlateIcon size={24} /> },
];

export const DrawerNavigation: React.FC<DrawerNavigationProps> = ({
  currentPage,
  onNavigate,
  collapsed = false,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}) => {
  const isAdmin = checkIsAdmin();
  const currentDrawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  // Show "My Tasks" for annotators, "Dashboard" for admins
  const mainNavLabel = isAdmin ? 'Dashboard' : 'My Tasks';

  const renderNavItem = (item: NavItem, isCollapsed: boolean) => (
    <ListItem key={item.page} disablePadding>
      <ListItemButton
        selected={currentPage === item.page || (item.page === 'statsMmc' && currentPage === 'statsMmcModelYear')}
        onClick={() => {
          onMobileClose?.();
          onNavigate(item.page);
        }}
        sx={{
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          px: isCollapsed ? 1 : 2,
          pl: !isCollapsed && item.indent ? 4 : undefined,
        }}
      >
        <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 56, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontSize: item.indent ? '0.875rem' : undefined,
              color: item.indent ? 'text.secondary' : undefined,
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );

  const drawerContent = (isCollapsed: boolean, showCollapseButton: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        {!isCollapsed && (
          <Typography variant="h6" noWrap component="div">
            Annotate
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <List>
        {/* Main nav with dynamic label based on role */}
        {renderNavItem({ page: 'dashboard', label: mainNavLabel, icon: <HomeIcon /> }, isCollapsed)}
        {/* Task Management - admin only */}
        {isAdmin && renderNavItem({ page: 'taskManagement', label: 'Task Management', icon: <SettingsIcon /> }, isCollapsed)}
      </List>
      {isAdmin && (
        <>
          <Divider />
          <List
            subheader={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  gap: 1,
                  px: isCollapsed ? 0 : 2,
                  py: 1,
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                {!isCollapsed && (
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    Search / Edit
                  </Typography>
                )}
              </Box>
            }
          >
            {searchNavItems.map((item) => renderNavItem(item, isCollapsed))}
          </List>
          <Divider />
          <List
            subheader={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCollapsed ? 'center' : 'flex-start',
                  gap: 1,
                  px: isCollapsed ? 0 : 2,
                  py: 1,
                }}
              >
                <BarChartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                {!isCollapsed && (
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                    Statistics
                  </Typography>
                )}
              </Box>
            }
          >
            {statsNavItems.map((item) => renderNavItem(item, isCollapsed))}
          </List>
        </>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              const next = getThemeMode() === 'light' ? 'dark' : 'light';
              setThemeMode(next);
              window.dispatchEvent(new Event('theme:changed'));
            }}
            sx={{
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              px: isCollapsed ? 1 : 2,
            }}
          >
            <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 56, justifyContent: 'center' }}>
              {getThemeMode() === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText primary={getThemeMode() === 'dark' ? 'Light Mode' : 'Dark Mode'} />
            )}
          </ListItemButton>
        </ListItem>
      </List>
      {showCollapseButton && onToggleCollapse && (
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={onToggleCollapse}
              sx={{
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                px: isCollapsed ? 1 : 2,
              }}
            >
              <ListItemIcon sx={{ minWidth: isCollapsed ? 0 : 56, justifyContent: 'center' }}>
                {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </ListItemIcon>
              {!isCollapsed && <ListItemText primary="Collapse" />}
            </ListItemButton>
          </ListItem>
        </List>
      )}
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {drawerContent(false, false)}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: currentDrawerWidth,
            transition: 'width 0.2s',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent(collapsed, true)}
      </Drawer>
    </>
  );
};

export default DrawerNavigation;
